<template>
  <div :class="{ 'disabled-area': !$can('investor_edit') }">
    <v-form ref="investorForm" lazy-validation>
      <v-row no-gutters class="my-5">
        <v-col cols="12" sm="6" md="4">
          <v-text-field
            v-model="credentials.email"
            :label="$t('common.fields.email') + ' *'"
            :placeholder="$t('common.fields.emailPlaceholder')"
            :rules="rules.email"
            type="text"
            class="ma-2"
            autocomplete="off"
          />
          <v-text-field
            v-model="credentials.name"
            :label="$t('common.fields.investorFullName') + ' *'"
            :placeholder="$t('common.fields.investorNamePlaceholder')"
            :rules="rules.name"
            :disabled="isNameDisabled"
            type="text"
            class="ma-2"
            autocomplete="off"
          />
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <v-text-field
            v-model="credentials.phone"
            :label="$t('common.fields.phone') + ' *'"
            :placeholder="$t('common.fields.phonePlaceholder')"
            :rules="rules.phone"
            v-mask="'+7##########'"
            type="text"
            class="ma-2"
            autocomplete="off"
          />
          <v-text-field
            v-model="credentials.extraPhone"
            :label="$t('common.fields.extraPhone')"
            :placeholder="$t('common.fields.phonePlaceholder')"
            v-mask="'+7##########'"
            type="text"
            class="ma-2"
            autocomplete="off"
          />
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <div v-if="isEdit" class="ma-2">
            <h3 class="mb-2">
              {{ $t('common.blocks.investorPassword') + ' *' }}
            </h3>
            <v-label>**********</v-label>
            <div class="mt-3">
              <v-btn
                type="button"
                class="warning mr-5"
                depressed
                @click="changePasswordDialog = true"
                >{{ $t('setPassword.button') }}</v-btn
              >
            </div>
          </div>
          <div v-else>
            <v-text-field
              name="password"
              :placeholder="$t('common.fields.passwordPlaceholder') + '*'"
              v-model="credentials.password"
              class="ma-2"
              :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
              :type="showPassword ? 'text' : 'password'"
              :rules="rules.password"
              @click:append="showPassword = !showPassword"
              required
            />
            <v-text-field
              name="repeatPassword"
              :placeholder="$t('common.fields.repeatPlaceholder') + '*'"
              v-model="credentials.passwordConfirmation"
              class="ma-2"
              :append-icon="
                showPasswordConfirmation ? 'mdi-eye' : 'mdi-eye-off'
              "
              :type="showPasswordConfirmation ? 'text' : 'password'"
              :rules="rules.repeatPassword"
              @click:append="
                showPasswordConfirmation = !showPasswordConfirmation
              "
              required
            />
          </div>
        </v-col>
      </v-row>
      <v-row no-gutters class="my-5">
        <v-col cols="12" sm="6" md="4">
          <v-text-field
            v-model="credentials.credit_cards_data"
            :label="$t('investor-report.cardsData')"
            class="ma-2"
            autocomplete="off"
          />
          <v-text-field
            v-model="credentials.inn"
            :label="$t('investor-report.inn')"
            class="ma-2"
            autocomplete="off"
          />
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <v-text-field
            v-model="credentials.ip_too"
            :label="$t('investor-report.ip-too')"
            class="ma-2"
            autocomplete="off"
          />
          <v-select
            :items="locations"
            v-model="credentials.locationId"
            :label="$t('common.fields.region') + ' *'"
            item-text="title"
            item-value="id"
            :hide-details="false"
            class="ma-2"
          />
        </v-col>
        <v-col
          cols="12"
          :class="{
            'col-md-8': !isEdit || !investorCompanies.length,
            'col-md-4': isEdit && investorCompanies.length,
          }"
        >
          <v-textarea
            v-model="credentials.address"
            :label="$t('common.fields.address') + ' *'"
            :placeholder="$t('common.fields.addressPlaceholder')"
            :rules="rules.address"
            rows="2"
            type="text"
            class="ma-2"
            autocomplete="off"
          />
        </v-col>
        <v-card
          v-if="isEdit && investorCompanies.length"
          sm="6"
          md="4"
          class="ma-2"
        >
          <v-list disabled flat subheader shaped dense>
            <v-subheader>{{ $t('companiesList.title') }}</v-subheader>
            <v-list-item
              v-for="company in investorCompanies"
              v-bind:key="company"
              class="companyItem"
            >
              <v-list-item-content>
                {{ company }}
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card>
      </v-row>
      <h3 class="weight ma-2">{{ $t('common.labels.passport') }}</h3>
      <div class="d-flex flex-wrap">
        <div
          v-for="(photo, index) in credentials.photos"
          :key="index"
          class="col-xl-2 col-md-3 col-sm-6 cols-12"
        >
          <div class="img-uploader">
            <image-uploader-new
              :photo.sync="credentials.photos[index]"
              outlined
              tile
            />
          </div>
        </div>
        <div
          v-if="credentials.photos.length < 20"
          class="col-xl-2 col-md-3 col-sm-6 cols-12"
        >
          <div class="img-uploader">
            <image-uploader-new
              :photo.sync="credentials.photos[credentials.photos.length]"
              :rules="rules.personalityDocPhoto"
              outlined
              tile
            />
          </div>
        </div>
      </div>
      <!-- SUBMIT BUTTON -->
      <v-row class="d-flex align-end flex-column">
        <v-flex xs12>
          <!-- Error Alert -->
          <alert-error class="px-3" />
          <!-- Success Alert-->
          <v-layout
            v-if="successAlert.state"
            row
            class="alert-wrapper mt-2 mb-2 px-3"
          >
            <v-flex sm12>
              <v-alert :value="successAlert.state" type="success">
                {{ successAlert.message }}
              </v-alert>
            </v-flex>
          </v-layout>
        </v-flex>
        <v-row>
          <div v-if="isEdit">
            <v-btn
              v-if="$can('investor_edit')"
              large
              depressed
              :disabled="areBtnsDisabled"
              :color="getStatusButtonColor()"
              class="ma-2"
              @click="toggleStatusDialog = true"
            >
              {{ getStatusButtonLabel() }}
            </v-btn>
          </div>
          <v-btn
            v-if="$can('investor_edit')"
            type="button"
            :disabled="areBtnsDisabled"
            class="warning ma-2"
            depressed
            large
            @click="saveInvestor"
            >{{ $t('common.btns.save') }}</v-btn
          >
        </v-row>
      </v-row>
      <v-overlay :value="isLoading">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
    </v-form>
    <change-password-dialog
      v-if="isEdit"
      :dialog.sync="changePasswordDialog"
      :investorId="investorId"
    />
    <toggle-status-dialog
      v-if="isEdit"
      :dialog.sync="toggleStatusDialog"
      :investorId="investorId"
      :status.sync="credentials.status"
    />
  </div>
</template>

<script>
/* eslint-disable */

import { mapGetters, mapActions, mapMutations } from 'vuex';
import { mask } from 'vue-the-mask';
import ToggleStatusDialog from '@/components/investor/ToggleStatusDialog.vue';
import ChangePasswordDialog from '@/components/user/ChangePasswordDialog.vue';
import AlertError from '@/components/shared/AlertError.vue';
import ImageUploaderNew from '@/components/shared/ImageUploader.vue';

export default {
  name: 'InvestorForm',
  directives: {
    mask,
  },
  components: {
    AlertError,
    ToggleStatusDialog,
    ImageUploaderNew,
    ChangePasswordDialog,
  },
  data() {
    return {
      areBtnsDisabled: false,
      isEdit: false,
      toggleStatusDialog: false,
      showPassword: false,
      showPasswordConfirmation: false,
      changePasswordDialog: false,
      credentials: {
        email: null,
        phone: null,
        extraPhone: null,
        password: null,
        passwordConfirmation: null,
        name: null,
        locationId: 12,
        address: null,
        status: 1,
        photos: [],
        credit_cards_data: '',
        inn: '',
        ip_too: '',
      },
      rules: {
        email: [
          (v) => !!v || this.$t('common.fields.validation.field.required'),
          (v) =>
            /(^$|^(([^<>()[\]\\.,;:а-яА-Я\s@"]+(\.[^<>()[\]\\.,;а-яА-Я:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$)/.test(
              v,
            ) || this.$t('common.fields.validation.email'),
        ],
        phone: [
          (v) => !!v || this.$t('common.fields.validation.field.required'),
          (v) =>
            /^[+][0-9]{9,12}$/.test(v) ||
            this.$t('common.fields.validation.phone'),
        ],
        password: [
          (v) => !!v || this.$t('common.fields.validation.field.required'),
          (v) =>
            /^[a-zа-яәғёқңөұүһіA-ZА-ЯӘҒЁҚҢӨҰҮҺІ0-9!@#$%^&*)(+=._-]{8,}$/.test(
              v,
            ) || this.$t('common.fields.validation.password'),
        ],
        repeatPassword: [
          (v) =>
            v === this.credentials.password ||
            this.$t('common.fields.validation.password.dontMatch'),
        ],
        name: [
          (v) => !!v || this.$t('common.fields.validation.field.required'),
          (v) =>
            /^[a-zа-яәғёқңөұүһіA-ZА-ЯӘҒЁҚҢӨҰҮҺІ /-]{1,100}$/.test(v) ||
            this.$t('common.fields.validation.director'),
        ],
        address: [
          (v) => !!v || this.$t('common.fields.validation.field.required'),
          (v) =>
            /^[a-zа-яәғёқңөұүһіA-ZА-ЯӘҒЁҚҢӨҰҮҺІ0-9!/ @#$%^&*)(+=.,_-]{1,100}$/.test(
              v,
            ) || this.$t('common.fields.validation.companyAddress'),
        ],
        personalityDocPhoto: [
          (v) =>
            !!v ||
            this.credentials.photos.length > 0 ||
            this.$t('common.fields.validation.photo.required'),
        ],
      },
      investorCompanies: [],
    };
  },
  props: {
    investorId: String,
  },
  computed: {
    ...mapGetters({
      locations: 'investor/getInvestorsLocations',
      successAlert: 'global/getAlertSuccess',
      isLoading: 'global/getLoader',
    }),
    isNameDisabled() {
      const isEdit = Boolean(this.investorId);
      return isEdit && !this.$can('investor_edit_name');
    },
  },
  watch: {
    // eslint-disable-next-line
    'credentials.photos': function() {
      this.credentials.photos.forEach((photo, index) => {
        if (photo === null) {
          this.credentials.photos.splice(index, 1);
        }
      });
    },
  },
  beforeCreate() {
    this.$store.commit('global/setLoader', true);
  },
  async created() {
    await this.fetchInvestorsProperties();
    if (this.investorId) {
      // get investor data
      const { investorId } = this;
      try {
        const response = await this.getInvestor(investorId);
        this.isEdit = true;
        if (response.success && response.data) {
          const investorCredentials = this.prepareInputData(response.data);
          this.credentials = investorCredentials;
        }
      } catch (error) {
        if (error.status === 404) {
          this.$router.push({ name: 'not-found' });
        }
        this.setLoader(false);
      }
    }
    this.setLoader(false);
  },
  destroyed() {
    this.clearAlertError();
    this.clearAlertSuccess();
  },
  methods: {
    ...mapActions({
      fetchInvestorsProperties: 'investor/fetchInvestorsProperties',
      createInvestor: 'investor/createInvestor',
      getInvestor: 'investor/fetchInvestorData',
      editInvestor: 'investor/editInvestor',
    }),
    ...mapMutations({
      clearAlertError: 'global/clearAlertError',
      clearAlertSuccess: 'global/clearAlertSuccess',
      setAlertSuccess: 'global/setAlertSuccess',
      setLoader: 'global/setLoader',
    }),
    async saveInvestor() {
      const isValidPhotos = this.validatePhotos();
      const isValidForm = this.$refs.investorForm.validate();
      if (isValidPhotos && isValidForm) {
        this.areBtnsDisabled = true;
        let response = null;
        const outData = this.prepareOutData();
        try {
          if (this.isEdit) {
            const investorData = {
              investorId: this.investorId,
              data: outData,
            };
            response = await this.editInvestor(investorData);
          } else {
            response = await this.createInvestor(outData);
          }
          if (response.success) {
            const successAlert = {
              state: true,
              message: this.$t('investor.saving.success'),
            };
            if (response.data.message) {
              successAlert.message = response.data.message;
            }
            this.clearAlertError();
            this.setAlertSuccess(successAlert);
            const that = this;
            setTimeout(() => {
              that.clearAlertSuccess();
              that.$router.push({ name: 'investors-list' });
            }, 2500);
          }
        } catch (error) {
          this.areBtnsDisabled = false;
        }
      }
    },
    // call validate method on image-uploader-new components
    validatePhotos() {
      let isValid = true;
      this.$children[0].$children.forEach((element) => {
        // eslint-disable-next-line
        if (
          element.$options['_componentTag'] &&
          element.$options['_componentTag'] === 'image-uploader-new'
        ) {
          if (!element.validate()) {
            isValid = false;
          }
        }
      });
      return isValid;
    },
    prepareOutData() {
      const data = { ...this.credentials };
      const { photos } = data;
      const outPhotos = [];
      photos.forEach((photo) => {
        if (photo) {
          outPhotos.push(photo.name);
        }
      });
      data.photos = outPhotos;
      if (data.extraPhone === '+7') {
        data.extraPhone = null;
      }
      return data;
    },
    prepareInputData(data) {
      const outData = data;
      delete outData.id;
      delete outData.userId;
      delete outData.createdAt;
      delete outData.updatedAt;
      const outPhotos = [];
      if (outData.photos.length > 0) {
        outData.photos.forEach((photo) => {
          outPhotos.push(photo);
        });
      }
      outData.photos = outPhotos;
      this.investorCompanies = outData.companies;
      delete outData.companies;

      return outData;
    },
    getStatusButtonColor() {
      if (this.credentials.status === 1) {
        return 'red lighten-1';
      }
      return 'light-green darken-1';
    },
    getStatusButtonLabel() {
      if (this.credentials.status === 1) {
        return this.$t('investor.label.deactivationInvestor');
      }
      return this.$t('investor.label.activationInvestor');
    },
  },
};
</script>
<style lang="scss" scoped>
h3 {
  margin-top: 15px;
  font-weight: 300;
}
.img-uploader {
  height: 250px;
}
.companyItem {
  min-height: 35px;
  .v-list-item__content {
    padding: 5px 0;
  }
}
</style>
